import React from 'react';
import { XIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import { Document } from '../types/condition';
type DocumentPreviewModalProps = {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
};
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function DocumentPreviewModal({
  document,
  isOpen,
  onClose
}: DocumentPreviewModalProps) {
  if (!isOpen || !document) return null;
  const isPDF = document.type === 'application/pdf';
  const isImage = document.type.startsWith('image/');
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="preview-title" data-id="element-206">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-207" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-208">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-209">
          <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-210">
            <div className="p-2 bg-blue-100 rounded" data-id="element-211">
              <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-212" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-213">
              <h2 id="preview-title" className="text-lg font-semibold text-gray-900 truncate" data-id="element-214">
                {document.name}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-215">
                {formatFileSize(document.size)} • Uploaded by{' '}
                {document.uploadedBy} • {formatDate(document.uploadedAt)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4" data-id="element-216">
            <button onClick={() => {}} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors" aria-label="Download document" title="Download" data-id="element-217">
              <DownloadIcon className="w-5 h-5" data-id="element-218" />
            </button>
            <button onClick={onClose} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors" aria-label="Close preview" data-id="element-219">
              <XIcon className="w-5 h-5" data-id="element-220" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-gray-100 p-6" data-id="element-221">
          {isPDF ? <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-id="element-222">
              <FileTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-223" />
              <p className="text-gray-600 mb-4" data-id="element-224">
                PDF preview would appear here in production
              </p>
              <p className="text-sm text-gray-500" data-id="element-225">
                In a real application, this would use a PDF viewer library like
                react-pdf or pdf.js
              </p>
            </div> : isImage ? <div className="flex items-center justify-center" data-id="element-226">
              <img src={document.url} alt={document.name} className="max-w-full max-h-[70vh] rounded-lg shadow-lg" data-id="element-227" />
            </div> : <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-id="element-228">
              <FileTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-229" />
              <p className="text-gray-600 mb-2" data-id="element-230">Preview not available</p>
              <p className="text-sm text-gray-500" data-id="element-231">
                This file type cannot be previewed. Please download to view.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}